import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Monitor, QrCode, Smartphone, Zap, Trophy, Wand2, Keyboard,
  LifeBuoy, Timer, Users, Play, ChevronRight, Sparkles, Flag,
} from "lucide-react";

/*
 * Page /doc — guide opérateur, en anglais (même règle que l'UI admin).
 *
 * Reprend le PDF "How to start the kiosk with Think and Sprint" et l'étoffe avec
 * ce que le PDF ne couvrait pas : règles de départ de course, barème, création de
 * quiz, raccourcis kiosque, dépannage. À garder synchro quand ces règles bougent
 * (notamment MIN_PLAYERS_FOR_COUNTDOWN / COUNTDOWN_SECONDS dans server/index.js).
 */

// Ancres de la table des matières. Doit rester aligné sur les id des <Section>.
const TOC = [
  { id: "start", label: "Run the kiosk" },
  { id: "race", label: "How a race starts" },
  { id: "playing", label: "Playing & scoring" },
  { id: "create", label: "Create your own quiz" },
  { id: "shortcuts", label: "Operator shortcuts" },
  { id: "trouble", label: "Troubleshooting" },
];

function Section({
  id,
  icon: Icon,
  title,
  children,
}: {
  id: string;
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <motion.section
      id={id}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ type: "spring", bounce: 0.3 }}
      className="scroll-mt-6 bg-white/80 backdrop-blur-xl rounded-blob shadow-kawaii-card border-2 border-sakura-200/40 p-6 sm:p-8"
    >
      <h2 className="flex items-center gap-3 text-2xl font-black text-sakura-500 mb-5">
        <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-sakura-200 to-lavender-200 flex items-center justify-center shadow-kawaii-card shrink-0">
          <Icon className="w-6 h-6 text-white" />
        </span>
        {title}
      </h2>
      <div className="space-y-4 text-gray-600 font-bold leading-relaxed">
        {children}
      </div>
    </motion.section>
  );
}

// Étape numérotée de la marche à suivre kiosque.
function Step({ n, title, children }: { n: number; title: string; children?: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <span className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-sakura-400 to-lavender-400 text-white font-black flex items-center justify-center shadow-kawaii-card">
        {n}
      </span>
      <div className="pt-1">
        <p className="text-gray-700 font-extrabold">{title}</p>
        {children && <p className="text-sm text-gray-500 font-bold mt-1">{children}</p>}
      </div>
    </div>
  );
}

// Encart d'accent : "tip" (lavande) ou "warn" (ambre).
function Note({ tone = "tip", children }: { tone?: "tip" | "warn"; children: React.ReactNode }) {
  const styles =
    tone === "warn"
      ? "bg-star/10 border-star/40 text-amber-700"
      : "bg-lavender-50 border-lavender-200 text-lavender-600";
  return (
    <div className={`rounded-kawaii border-2 p-4 text-sm font-bold ${styles}`}>
      {children}
    </div>
  );
}

function Key({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[2rem] px-2 py-1 rounded-lg bg-white border-2 border-lavender-200 text-lavender-500 font-black text-sm shadow-kawaii-card">
      {children}
    </kbd>
  );
}

export function DocPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen font-kawaii bg-gradient-to-br from-sakura-100 via-lavender-100 to-sky-100 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.08]"
        style={{
          backgroundImage: "url(/assets/bg_grid.svg)",
          backgroundSize: "200px 200px",
          backgroundRepeat: "repeat",
        }}
      />
      <div className="absolute top-[-12%] left-[-8%] w-[380px] h-[380px] rounded-full bg-sakura-200/40 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-8%] w-[340px] h-[340px] rounded-full bg-lavender-200/40 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-3xl mx-auto px-4 py-10 sm:py-14">
        {/* --- Hero --- */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ type: "spring", bounce: 0.4 }}
          className="text-center mb-10"
        >
          <motion.div
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            className="inline-block mb-4"
          >
            <img src="/assets/logo.svg" alt="Think&Sprint" className="h-20 mx-auto" />
          </motion.div>
          <h1 className="text-4xl sm:text-5xl font-black text-sakura-500 mb-3">
            Host a race
          </h1>
          <p className="text-lavender-500 font-bold text-lg max-w-xl mx-auto">
            Everything you need to run Think&amp;Sprint on a big screen: set up the
            kiosk, start a race, build your own quiz.
          </p>
        </motion.div>

        {/* --- Table des matières --- */}
        <motion.nav
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15 }}
          className="flex flex-wrap justify-center gap-2 mb-10"
        >
          {TOC.map((t) => (
            <a
              key={t.id}
              href={`#${t.id}`}
              className="px-4 py-2 rounded-full bg-white/70 border-2 border-lavender-200/60 text-lavender-500 font-bold text-sm hover:bg-white hover:border-sakura-300 hover:text-sakura-500 transition-colors"
            >
              {t.label}
            </a>
          ))}
        </motion.nav>

        <div className="space-y-6">
          {/* --- 1. Kiosk --- */}
          <Section id="start" icon={Monitor} title="Run the kiosk">
            <p>
              A race needs one shared screen (the <strong>kiosk</strong>) and one phone
              per player. The kiosk shows the quiz, the QR code and the 3D race; phones
              are the controllers.
            </p>
            <div className="space-y-4 pt-1">
              <Step n={1} title="Pick a computer with a stable internet connection">
                Any modern browser works. Connect it to a large screen or a projector if
                you can: everyone will be looking at it.
              </Step>
              <Step n={2} title="Open Think&Sprint on that computer" />
              <Step n={3} title="Find your quiz on the home page and click its card">
                Not there? Click <em>Create your own quiz</em> to build one, then use the
                share link it gives you.
              </Step>
              <Step n={4} title="The trailer screen appears">
                Animals running, the quiz title, the current record and a QR code. This
                screen is already live: the race exists and is waiting for players.
              </Step>
              <Step n={5} title="Ask players to scan the QR code at the bottom" />
              <Step n={6} title="Each player types a nickname on their phone">
                Up to 10 characters. It shows above their avatar during the race.
              </Step>
              <Step n={7} title="As soon as one player taps Go, the big screen switches to the lobby">
                Player cards appear with the 3D avatar each person was assigned.
              </Step>
              <Step n={8} title="Players tap Ready when they want to start" />
              <Step n={9} title="Have fun" />
            </div>
            <Note>
              Both QR codes point to the same race, so it doesn't matter whether people
              scan from the trailer screen or from the lobby.
            </Note>
          </Section>

          {/* --- 2. Départ de course --- */}
          <Section id="race" icon={Timer} title="How a race starts">
            <p>
              The race never starts behind a player's back. Three rules decide when it
              goes:
            </p>
            <div className="grid gap-3">
              <div className="rounded-kawaii bg-white border-2 border-sakura-100 p-4 flex gap-3">
                <Users className="w-5 h-5 text-sakura-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-700 font-extrabold">Alone in the lobby</p>
                  <p className="text-sm text-gray-500 font-bold">
                    Tapping Ready does <strong>not</strong> start a countdown. The phone
                    shows <em>Waiting for other players</em>, so nobody accidentally locks
                    everyone else out of the race. There is a{" "}
                    <em>Start solo now</em> button for practising alone.
                  </p>
                </div>
              </div>
              <div className="rounded-kawaii bg-white border-2 border-sakura-100 p-4 flex gap-3">
                <Timer className="w-5 h-5 text-sakura-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-700 font-extrabold">Two players or more</p>
                  <p className="text-sm text-gray-500 font-bold">
                    A 30-second countdown starts as soon as at least one of them is
                    ready. Anyone joining while it runs pushes it back up to 20 seconds,
                    so latecomers always get time to pick a nickname.
                  </p>
                </div>
              </div>
              <div className="rounded-kawaii bg-white border-2 border-sakura-100 p-4 flex gap-3">
                <Zap className="w-5 h-5 text-sakura-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-gray-700 font-extrabold">Everybody ready</p>
                  <p className="text-sm text-gray-500 font-bold">
                    With two or more players all ready, the countdown is skipped and the
                    race starts immediately.
                  </p>
                </div>
              </div>
            </div>
            <Note>
              People can keep joining until the race actually starts. If someone misses
              it, they just scan again: the kiosk returns to the trailer screen about 30
              seconds after the finish and a fresh race is waiting.
            </Note>
          </Section>

          {/* --- 3. Jeu et barème --- */}
          <Section id="playing" icon={Flag} title="Playing & scoring">
            <p>
              Each question shows on every phone at the same time. Answer fast: both your
              score and how far your avatar sprints depend on how quickly you pick.
            </p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="rounded-kawaii bg-gradient-to-br from-mint/20 to-white border-2 border-mint/40 p-4 text-center">
                <p className="text-2xl font-black text-emerald-600">1000</p>
                <p className="text-xs font-bold text-gray-500 mt-1">
                  points for an instant correct answer
                </p>
              </div>
              <div className="rounded-kawaii bg-gradient-to-br from-star/20 to-white border-2 border-star/40 p-4 text-center">
                <p className="text-2xl font-black text-amber-600">100</p>
                <p className="text-xs font-bold text-gray-500 mt-1">
                  points if you answer on the last second
                </p>
              </div>
              <div className="rounded-kawaii bg-gradient-to-br from-sakura-100 to-white border-2 border-sakura-200 p-4 text-center">
                <p className="text-2xl font-black text-sakura-500">-500</p>
                <p className="text-xs font-bold text-gray-500 mt-1">
                  points for a wrong answer, and one step back
                </p>
              </div>
            </div>
            <p className="text-sm">
              The curve is quadratic, not linear: hesitating costs a lot more than it
              looks. A correct answer moves you 1 to 10 steps forward. Default time per
              question is 20 seconds, adjustable per quiz.
            </p>
            <p>
              When the last player finishes, the screen flashes white and everyone is
              lined up on the <strong>podium</strong>, ranked by score, with confetti on
              the winner. Beat the quiz record and it is saved with your nickname, then
              displayed on the home page and on the trailer screen.
            </p>
          </Section>

          {/* --- 4. Création de quiz --- */}
          <Section id="create" icon={Wand2} title="Create your own quiz">
            <p>
              Click <em>Create your own quiz</em> on the home page. You will be asked to
              sign in, then you get two ways to build a race.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-kawaii bg-white border-2 border-lavender-200 p-4">
                <p className="font-black text-lavender-500 mb-1">One-shot sets</p>
                <p className="text-sm text-gray-500 font-bold">
                  A single quiz you share with a link. Give a topic, a language and a
                  number of questions, and they are generated for you. Review and edit
                  every question before saving.
                </p>
              </div>
              <div className="rounded-kawaii bg-white border-2 border-lavender-200 p-4">
                <p className="font-black text-lavender-500 mb-1">Schedules</p>
                <p className="text-sm text-gray-500 font-bold">
                  A series of sessions on a cadence (weekly, daily, monthly or custom
                  dates). One shared link always plays the session of the moment, which
                  suits a permanent display.
                </p>
              </div>
            </div>
            <p className="text-sm">
              Questions can be generated in English, French, German, Luxembourgish or
              Portuguese. Be specific in the topic field: <em>Ancient Egypt, easy level,
              8-10 year-olds</em> gives far better questions than <em>history</em>. You
              can also set how many of the generated questions are actually played per
              race, and the time allowed per question.
            </p>
            <Note tone="warn">
              Always skim the generated questions before a public session. Generation is
              fast and usually good, but it is a language model: it can be confidently
              wrong on niche facts.
            </Note>
          </Section>

          {/* --- 5. Raccourcis --- */}
          <Section id="shortcuts" icon={Keyboard} title="Operator shortcuts">
            <p className="text-sm">
              On the kiosk screen (<code className="text-sakura-500">/auto-room</code>),
              with no text field focused:
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { k: "S", d: "Switch to the spectator view of the current race" },
                { k: "C", d: "Copy the join link to the clipboard" },
                { k: "T", d: "Open a 4-player test window (dry runs)" },
                { k: "R", d: "Reload the screen" },
              ].map((s) => (
                <div
                  key={s.k}
                  className="flex items-center gap-3 rounded-kawaii bg-white border-2 border-lavender-100 p-3"
                >
                  <Key>{s.k}</Key>
                  <span className="text-sm text-gray-500 font-bold">{s.d}</span>
                </div>
              ))}
            </div>
            <p className="text-sm">
              A gamepad works too: button B switches to the spectator view.
            </p>
          </Section>

          {/* --- 6. Dépannage --- */}
          <Section id="trouble" icon={LifeBuoy} title="Troubleshooting">
            <div className="space-y-3">
              {[
                {
                  q: "Phones cannot open the QR code link",
                  a: "Phones and the kiosk must reach the same address. On a local network, put them on the same Wi-Fi. On a guest network that blocks device-to-device traffic, use the public URL instead.",
                },
                {
                  q: "A player scanned but nobody sees them",
                  a: "They must type a nickname and tap Go. Until then they are not in the lobby. If their phone says the room is no longer available, the screen was restarted: reload the kiosk and scan the new QR code.",
                },
                {
                  q: "The race started without me",
                  a: "Joining while the countdown runs pushes it back to 20 seconds, but it does not pause it. Wait for the next race: the kiosk comes back to the trailer screen roughly 30 seconds after the finish.",
                },
                {
                  q: "The screen is stuck on the trailer",
                  a: "That screen is idle on purpose until the first player taps Go. If someone did and nothing happened, press R to reload the kiosk.",
                },
                {
                  q: "The 3D race is choppy",
                  a: "The kiosk renders the full 3D scene. Close other tabs, and prefer a machine with a real GPU when you expect a lot of players.",
                },
              ].map((item) => (
                <div
                  key={item.q}
                  className="rounded-kawaii bg-white border-2 border-sakura-100 p-4"
                >
                  <p className="text-gray-700 font-extrabold mb-1">{item.q}</p>
                  <p className="text-sm text-gray-500 font-bold">{item.a}</p>
                </div>
              ))}
            </div>
          </Section>
        </div>

        {/* --- CTA final --- */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-10 rounded-blob bg-gradient-to-r from-sakura-400 via-sakura-500 to-lavender-400 p-8 text-center shadow-kawaii-lg"
        >
          <Sparkles className="w-10 h-10 text-white/90 mx-auto mb-3" />
          <p className="text-white font-black text-2xl mb-1">Ready to race?</p>
          <p className="text-white/80 font-bold mb-5">
            Pick a demo on the home page, or build your own quiz.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => navigate("/")}
              className="px-6 py-3 rounded-xl bg-white text-sakura-500 font-black shadow-kawaii-card flex items-center gap-2 hover:scale-105 transition-transform"
            >
              <Play className="w-5 h-5" />
              Back to home
            </button>
            <button
              onClick={() => navigate("/admin")}
              className="px-6 py-3 rounded-xl bg-white/20 border-2 border-white/50 text-white font-black flex items-center gap-2 hover:bg-white/30 transition-colors"
            >
              <Wand2 className="w-5 h-5" />
              Create a quiz
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        {/* Icônes importées pour la sémantique des sections mais non rendues ailleurs */}
        <p className="mt-8 text-center text-xs font-bold text-lavender-400/70 flex items-center justify-center gap-2">
          <QrCode className="w-4 h-4" />
          <Smartphone className="w-4 h-4" />
          <Trophy className="w-4 h-4" />
          Made with love by LMDDC · 3D assets by Kenney
        </p>
      </div>
    </div>
  );
}
